import React, { forwardRef, useEffect, useState, } from "react";
import { flip, offset, size, useClick, useDismiss, useFloating, useInteractions, useRole, } from "@floating-ui/react-dom-interactions";
import { Box, Portal } from "@chakra-ui/react";
import throttle from "lodash/throttle";
import FocusLock from "react-focus-lock";
import { useFocusVisible } from "react-aria";
export function usePopover({ placement, } = {}) {
    const [isOpen, setIsOpen] = useState(false);
    const { x, y, reference, floating, strategy, context, update } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: placement ?? "bottom",
        middleware: [
            flip(),
            offset(8),
            size({
                apply({ availableHeight, elements }) {
                    elements.floating.style.maxHeight = `${Math.min(320, availableHeight)}px`;
                },
            }),
        ],
    });
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const hide = () => {
            setIsOpen(false);
        };
        window.addEventListener("resize", hide, { once: true });
        window.addEventListener("scroll", hide, { once: true });
        return () => {
            window.removeEventListener("resize", hide);
            window.removeEventListener("scroll", hide);
        };
    }, [isOpen]);
    useEffect(() => {
        const updatePosition = throttle(update, 100, {
            leading: true,
        });
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition);
        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition);
        };
    }, []);
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useDismiss(context),
        useRole(context),
    ]);
    return {
        getTargetProps(props) {
            return {
                ref: reference,
                ...props,
                ...getReferenceProps(),
            };
        },
        getPopoverProps() {
            return {
                ref: floating,
                isOpen,
                left: `${x ?? 0}px`,
                top: `${y ?? 0}px`,
                position: strategy,
                ...getFloatingProps(),
            };
        },
    };
}
export const Popover = forwardRef(({ isOpen, children, ...props }, ref) => {
    const { isFocusVisible } = useFocusVisible();
    return (React.createElement(Portal, null,
        React.createElement(Box, { ref: ref, background: "white", border: "1px", borderColor: "gray.400", borderRadius: "md", boxShadow: "lg", outline: "none", display: "flex", flexDirection: "column", overflow: "auto", overscrollBehavior: "contain", transition: isOpen ? "opacity 200ms, transform 200ms" : undefined, transform: "auto", transformOrigin: "top", translateY: isOpen ? 0 : -2, opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? "auto" : "none", width: "max-content", maxWidth: "md", ...props },
            React.createElement(FocusLock, { returnFocus: true, disabled: !isOpen || !isFocusVisible }, children))));
});
